import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, GraduationCap } from 'lucide-react';
import { TAGLEVELS, INTL_LEVEL_VALUES } from '../utils/Constants';
import useLocalStorageLevel from '../contexts/levelHook';

// Turn a TAGLEVELS entry into a readable "exam" string, expanding submenu groups
// (VARSITY / ENGINEERING) into "Group (sub, sub, …)".
const describeItem = (item) =>
    item.submenu
        ? `${item.label} (${item.submenu.map((s) => s.label).join(', ')})`
        : item.label;

const INTL_EXAMS = TAGLEVELS.filter((i) => INTL_LEVEL_VALUES.includes(i.value)).map(describeItem);
const BD_EXAMS = TAGLEVELS.filter((i) => !INTL_LEVEL_VALUES.includes(i.value)).map(describeItem);

// A stylised exam-family card: gradient header (icon + eyebrow + title) over a
// list of ticked exam rows.
const ExamCard = ({ icon: Icon, eyebrow, title, subtitle, exams, gradient, tick }) => (
    <div className="kp-exam-card">
        <div style={{ background: gradient, color: '#ffffff', padding: '1.15rem 1.35rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <div style={{ width: '2.4rem', height: '2.4rem', borderRadius: '0.7rem', background: 'rgba(255,255,255,0.2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={22} />
                </div>
                <div>
                    <div style={{ fontSize: '0.66rem', fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase', opacity: 0.85 }}>
                        {eyebrow}
                    </div>
                    <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800 }}>{title}</h3>
                </div>
            </div>
            <p style={{ margin: '0.6rem 0 0', fontSize: '0.82rem', opacity: 0.9 }}>{subtitle}</p>
        </div>
        <div style={{ padding: '0.75rem 1.35rem 1.1rem' }}>
            {exams.map((label) => (
                <div key={label} className="kp-exam-row">
                    <span className="kp-exam-tick" style={{ background: tick }}>✓</span>
                    <span>{label}</span>
                </div>
            ))}
        </div>
    </div>
);

const HomePage = () => {
    const navigate = useNavigate();
    const [selectedLevel, , levelExtra] = useLocalStorageLevel();
    const { division: selectedDivision, unit: selectedUnit } = levelExtra;

    const currentSecondary = selectedDivision
        ? `Division: ${selectedDivision}`
        : (selectedUnit ? `Unit: ${selectedUnit}` : '');

    return (
        <div style={{ padding: '2rem', minHeight: '100vh', backgroundColor: '#f3f4f6' }}>
            <h1 style={{ fontSize: '2.25rem', lineHeight: '2.5rem', fontWeight: '800', color: '#111827', marginBottom: '2rem', textAlign: 'center' }}>
                Welcome to KobraPrep Model Test Platform
            </h1>

            <div style={{ maxWidth: '80rem', marginLeft: 'auto', marginRight: 'auto' }}>
                {selectedLevel ? (
                    <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#ffedd5', color: '#9a3412', borderRadius: '0.5rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)', textAlign: 'center', fontSize: '1.125rem', lineHeight: '1.75rem', fontWeight: '500' }}>
                        Currently Selected Level: <span style={{ fontWeight: '700' }}>{selectedLevel}</span>
                        {currentSecondary && <span style={{ fontWeight: '700' }}>{'  ·  ' + currentSecondary}</span>}
                    </div>
                ) : (
                    <div style={{ marginBottom: '1.5rem', padding: '1rem 1.25rem', backgroundColor: '#fef3c7', color: '#92400e', borderRadius: '0.5rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)', textAlign: 'center', fontSize: '1.05rem', lineHeight: '1.6rem', fontWeight: '500' }}>
                        No level selected yet. Pick a level from the top navigation bar to get started —
                        use <strong>“Select Level (Int'l exams)”</strong> or <strong>“Select Level (BD exams)”</strong>.
                        Only one level can be active at a time.
                    </div>
                )}

                {/* Which exams live under each family button */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.25rem', marginBottom: '1.75rem' }}>
                    <ExamCard
                        icon={Globe}
                        eyebrow="International"
                        title="Select Level (Int'l exams)"
                        subtitle="International / standardized tests. Levels & exams included:"
                        exams={INTL_EXAMS}
                        gradient="linear-gradient(145deg,#6366f1,#4338ca)"
                        tick="#4338ca"
                    />
                    <ExamCard
                        icon={GraduationCap}
                        eyebrow="Bangladeshi"
                        title="Select Level (BD exams)"
                        subtitle="Bangladeshi exams. Levels & exams included:"
                        exams={BD_EXAMS}
                        gradient="linear-gradient(145deg,#7c3aed,#5b21b6)"
                        tick="#5b21b6"
                    />
                </div>

                {/* How to take a test */}
                <div style={{ backgroundColor: '#f5f3ff', borderRadius: '0.75rem', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.08)', padding: '1.5rem', border: '1px solid #ddd6fe' }}>
                    <h2 style={{ fontSize: '1.4rem', fontWeight: '700', color: '#4c1d95', marginBottom: '1rem' }}>
                        How to take a test
                    </h2>
                    <ol style={{ margin: 0, paddingLeft: '1.4rem', color: '#374151', lineHeight: '2rem', fontSize: '1rem' }}>
                        <li>
                            Choose your exam level from the top navigation bar —
                            <strong> Select Level (Int'l exams)</strong> for IELTS / TOEFL / GRE / SAT, or
                            <strong> Select Level (BD exams)</strong> for SSC, HSC, BCS and the rest.
                            (For SSC/HSC pick a Division; for VARSITY pick a Unit.)
                        </li>
                        <li>
                            Open <strong>Model Test Store</strong> in the navbar to browse the tests and quizzes
                            available for your selected level.
                        </li>
                        <li>Start a test, answer the questions, and submit to see your score and review.</li>
                        <li>
                            Track your progress under <strong>My Stats</strong>, revisit saved questions in
                            <strong> Starred</strong>, and build vocabulary in the <strong>Vocabulary Store</strong>.
                        </li>
                    </ol>
                    <div style={{ marginTop: '1.25rem' }}>
                        <button
                            type="button"
                            onClick={() => navigate('/userquiz')}
                            style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#ffffff', border: 'none', borderRadius: '9999px', padding: '0.65rem 1.4rem', fontWeight: 700, fontSize: '1rem', cursor: 'pointer', boxShadow: '0 6px 14px rgba(76,29,149,0.3)' }}
                        >
                            Go to Model Test Store →
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HomePage;
